import { NegotiationSide, OverriddenNegotiationSide, EntityType, PlatformFeature } from 'api'
import { groupBy, isNil } from 'lodash'
import { createSelector } from '@reduxjs/toolkit'
import { createAllEntityUpdatesSelector } from 'selectors/entitySelectors'
import { createPlatformFeatureEnabledSelector } from 'selectors/platformSelectors'
import { negotiationStoreSelector } from 'selectors/storeSelectors'
import { isNotNil } from 'utils/predicate'
import {
  negotiationSideToOverridenNegotiationSide,
  negotiationOverrideUpdateVersionMatch,
  applyNegotiationOverrideToRepositoryEntry
} from './negotiationOverrideUtils'

const defaultNegotiationEntriesGetterSelector = createSelector(negotiationStoreSelector, negotiationStore => {
  return (negotiationSide: NegotiationSide) => negotiationStore[negotiationSide]
})

const overridableNegotiationEntriesGetterSelector = createSelector(
  negotiationStoreSelector,
  createAllEntityUpdatesSelector(EntityType.NEGOTIATION_OVERRIDE),
  (negotiationStore, negotiationOverrides) => {
    const overrides = Object.values(negotiationOverrides)
    const groupedOverrides = groupBy(overrides, e => e.entity.negotiationId)
    return (negotiationSide: NegotiationSide) => {
      const repositoryEntries = negotiationStore[negotiationSide]
      const mappedSide = negotiationSideToOverridenNegotiationSide(negotiationSide)

      return Object.entries(groupedOverrides).reduce(
        (repo, [negotiationId, overrideUpdates]) => {
          const negotiationUpdate = repo[negotiationId] ? repo[negotiationId].lastUpdate : undefined
          const [firstOverrideUpdate] = isNil(negotiationUpdate)
            ? []
            : overrideUpdates
                .filter(
                  overrideUpdate =>
                    [mappedSide, OverriddenNegotiationSide.BOTH].includes(overrideUpdate.entity.overriddenSide) &&
                    negotiationOverrideUpdateVersionMatch(negotiationUpdate, overrideUpdate)
                )
                .sort(a => (a.entity.overriddenSide === OverriddenNegotiationSide.BOTH ? 1 : -1))

          return isNotNil(firstOverrideUpdate)
            ? {
                ...repo,
                [negotiationId]: applyNegotiationOverrideToRepositoryEntry(repo[negotiationId], firstOverrideUpdate)
              }
            : repo
        },
        { ...repositoryEntries }
      )
    }
  }
)

export const negotiationRepositoryEntryGetterSelector = createSelector(
  createPlatformFeatureEnabledSelector(PlatformFeature.ENABLE_LIGHT_WEIGHT_PRICE),
  defaultNegotiationEntriesGetterSelector,
  overridableNegotiationEntriesGetterSelector,
  (lightweightPriceEnabled, defaultNegotiationEntriesGetter, overridableNegotiationEntriesGetter) => {
    return lightweightPriceEnabled ? overridableNegotiationEntriesGetter : defaultNegotiationEntriesGetter
  }
)
